import React from 'react';
import type { SearchQuery } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { translateLocationName } from '../utils/localizationUtils';

interface ResultsHeaderProps {
  searchQuery: SearchQuery;
  onBack: () => void;
}

const calculateCheckoutDate = (checkIn: string, nights: number): string => {
    const checkInDate = new Date(checkIn);
    checkInDate.setDate(checkInDate.getDate() + nights);
    const day = String(checkInDate.getDate()).padStart(2, '0');
    const month = String(checkInDate.getMonth() + 1).padStart(2, '0');
    const year = checkInDate.getFullYear();
    return `${day}.${month}.${year}`;
};

const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
}

export const ResultsHeader: React.FC<ResultsHeaderProps> = ({ searchQuery, onBack }) => {
    const { t, language } = useLocalization();

    const { destinationName, departureName, checkIn, nights, adults } = searchQuery;
    const checkoutDate = calculateCheckoutDate(checkIn, nights);
    const formattedCheckin = formatDate(checkIn);

    const checkinLabel = language === 'sv' ? 'Incheckning' : language === 'da' ? 'Check-in' : 'Check In';
    const checkoutLabel = language === 'sv' ? 'Utcheckning' : language === 'da' ? 'Check-out' : 'Check Out';
    const nightsLabel = language === 'sv' ? (nights === 1 ? 'natt' : 'nätter') : language === 'da' ? (nights === 1 ? 'nat' : 'nætter') : (nights === 1 ? 'Night' : 'Nights');
    const adultsLabel = language === 'sv' ? 'vuxna' : language === 'da' ? 'voksne' : 'Adults';
    const hotelsLabel = language === 'sv' ? 'Hotell' : language === 'da' ? 'Hoteller' : 'Hotels';

    const translatedDeparture = translateLocationName(departureName || '', language);
    const translatedDestination = translateLocationName(destinationName || '', language);

    const summaryText = `${translatedDeparture} - ${translatedDestination.toUpperCase()}, ${checkinLabel} ${formattedCheckin}, ${checkoutLabel} ${checkoutDate}, ${nights} ${nightsLabel}, ${adults} ${adultsLabel}`;

    return (
        <div className="flex justify-between items-center">
            <div>
                <h1 className="text-2xl font-bold text-gray-800">{translatedDestination.toUpperCase()} {hotelsLabel}</h1>
                <p className="text-sm text-gray-500 mt-1">{summaryText}</p>
            </div>
            <button 
                onClick={onBack}
                className="bg-amber-500 text-white font-bold py-2.5 px-6 rounded-md hover:bg-amber-600 transition-colors duration-300"
            >
                {t('search_again')}
            </button>
        </div>
    );
};
